// src/features/fx-info/components/FxVolatilityCone.tsx
//
// Pillar 1 — Realized Volatility Cone.
// Charts the currency's CURRENT trailing annualized volatility across a set of
// look-back windows (2W / 1M / 3M / 6M / 1Y) against the 5-year historical
// distribution (min / p25 / median / p75 / max) for that same window. This
// instantly reveals whether options are "cheap" (current below median) or
// "expensive" (current above p75). We map each point into [bottom, top] arrays
// so Recharts natively draws the shaded percentile bands via <Area/>.

import { useMemo } from 'react';
import {
    Area,
    CartesianGrid,
    ComposedChart,
    Line,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

import type { VolatilityConePoint } from '../api';

interface Props {
    data: VolatilityConePoint[];
}

export const FxVolatilityCone = ({ data }: Props) => {
    // Transform data for Recharts Area ranges [bottom, top].
    const chartData = useMemo(
        () =>
            data.map((d) => ({
                ...d,
                minMaxRange: [d.min_vol, d.max_vol],
                iqrRange: [d.p25_vol, d.p75_vol],
            })),
        [data],
    );

    const formatPct = (val: number) =>
        new Intl.NumberFormat('en-US', {
            style: 'percent',
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
        }).format(val);

    if (!data.length) {
        return (
            <div className="w-full h-full flex flex-col min-h-[350px] items-center justify-center text-sm text-slate-400 dark:text-slate-500">
                No volatility data available.
            </div>
        );
    }

    return (
        <div className="w-full h-full flex flex-col min-h-[350px]">
            <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-1">
                Realized Volatility Cone
            </h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-6">
                Current annualized volatility vs 5-year historical distribution.
            </p>
            <div className="flex-1 w-full">
                <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                        data={chartData}
                        margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                    >
                        <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="#334155"
                            opacity={0.2}
                            vertical={false}
                        />
                        <XAxis
                            dataKey="window_label"
                            tick={{ fontSize: 12, fill: '#64748b' }}
                            axisLine={false}
                            tickLine={false}
                        />
                        <YAxis
                            tickFormatter={formatPct}
                            tick={{ fontSize: 12, fill: '#64748b' }}
                            axisLine={false}
                            tickLine={false}
                        />

                        <Tooltip
                            content={({ active, payload, label }) => {
                                if (active && payload && payload.length) {
                                    const raw = payload[0]
                                        .payload as VolatilityConePoint;
                                    return (
                                        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-3 rounded-lg shadow-xl text-sm min-w-[200px]">
                                            <p className="font-bold text-slate-900 dark:text-white mb-3 border-b border-slate-100 dark:border-slate-700 pb-2">
                                                {label} Horizon
                                            </p>
                                            <div className="flex flex-col gap-1.5 text-slate-600 dark:text-slate-300">
                                                <div className="flex justify-between">
                                                    <span className="text-slate-400">
                                                        Max:
                                                    </span>
                                                    <span>
                                                        {formatPct(raw.max_vol)}
                                                    </span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-slate-400">
                                                        75th Pct:
                                                    </span>
                                                    <span>
                                                        {formatPct(raw.p75_vol)}
                                                    </span>
                                                </div>
                                                <div className="flex justify-between items-center bg-indigo-50 dark:bg-indigo-900/20 px-2 py-1 -mx-2 rounded font-bold text-indigo-700 dark:text-indigo-400 border border-indigo-100 dark:border-indigo-800/50">
                                                    <span>Current:</span>
                                                    <span>
                                                        {formatPct(
                                                            raw.current_vol,
                                                        )}
                                                    </span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-slate-400">
                                                        Median:
                                                    </span>
                                                    <span>
                                                        {formatPct(
                                                            raw.median_vol,
                                                        )}
                                                    </span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-slate-400">
                                                        25th Pct:
                                                    </span>
                                                    <span>
                                                        {formatPct(raw.p25_vol)}
                                                    </span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-slate-400">
                                                        Min:
                                                    </span>
                                                    <span>
                                                        {formatPct(raw.min_vol)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                }
                                return null;
                            }}
                        />

                        {/* The broad Min-Max range */}
                        <Area
                            type="monotone"
                            dataKey="minMaxRange"
                            fill="#94a3b8"
                            fillOpacity={0.15}
                            stroke="none"
                            isAnimationActive={false}
                        />
                        {/* The IQR (25th to 75th percentile) range */}
                        <Area
                            type="monotone"
                            dataKey="iqrRange"
                            fill="#6366f1"
                            fillOpacity={0.15}
                            stroke="none"
                            isAnimationActive={false}
                        />

                        <Line
                            type="monotone"
                            dataKey="median_vol"
                            stroke="#64748b"
                            strokeWidth={2}
                            strokeDasharray="4 4"
                            dot={false}
                            isAnimationActive={false}
                        />
                        <Line
                            type="monotone"
                            dataKey="current_vol"
                            stroke="#6366f1"
                            strokeWidth={3}
                            dot={{
                                r: 4,
                                strokeWidth: 2,
                                fill: '#fff',
                                stroke: '#6366f1',
                            }}
                            activeDot={{ r: 6 }}
                            isAnimationActive={false}
                        />
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

export default FxVolatilityCone;
